/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalStorageKeys, PageTypeSuffix } from '@/constants'
import { useStore } from '@/containers'
import { useLocalStorage } from '@/hooks'
import { useDebounce } from '@/hooks/useDebounce'
import { CloseSearchIcon, SearchIcon } from '@/styles/Icon'
import { type Product } from '@/types'
import { api } from '@/utils'
import { getTranslate } from '@/utils/api-interceptors'
import { getProductWithPaginate } from '@/utils/endpoint'
import { useClickOutsideComponent } from '@/utils/useClickOutsideComponent'
import uniq from 'lodash/uniq'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { SearchProductItemCard } from '../SearchProductItemCard'

export const SearchBar = () => {
  const maxItemsReached = 3
  const [searchBarValue, setSearchBarValue] = useState('')
  const { ref, isComponentVisible, setIsComponentVisible } =
    useClickOutsideComponent(false)
  const router = useRouter()
  const { selectedLang } = useStore()

  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'USD'
      default:
        return 'VND'
    }
  }
  const debouncedSearchBarValue = useDebounce(searchBarValue, 500)
  const [searchHistory, setSearchHistory] = useLocalStorage<string[]>(
    'searchHistory',
    []
  )
  const [displayResult, setDisplayResult] = useState<{
    products: Product[] | null | undefined
  } | null>(null)

  const inputRef = useRef<any>(null)

  const handleInputKeyUp = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (searchBarValue === '') {
        localStorage?.removeItem(LocalStorageKeys.ITEM_LISTING_SEARCH)
      }
    }
    if (event.key === 'Enter') {
      setIsComponentVisible(false)
      if (searchHistory) {
        setSearchHistory(uniq(searchHistory.concat([searchBarValue])))
      }
      const res = await api(`api/keyword-cat?keyword=${searchBarValue}`, {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
      })
      if (res && !(res instanceof Error) && res.data) {
        router.push(
          getTranslate(res?.data?.slugs?.value) +
            PageTypeSuffix.CATEGORY +
            `?query=${searchBarValue}`
        )
      } else {
        return router.push(`/search-result?query=${searchBarValue}`)
      }
    }
  }

  useEffect(() => {
    if (router.query && router.query.query) {
      setSearchBarValue(router.query.query as string)
    }
  }, [router.query])

  useEffect(() => {
    const query = router.query
    if (debouncedSearchBarValue && query) {
      Promise.all([
        getProductWithPaginate({
          ...query,
          fullTextSearch: debouncedSearchBarValue,
          limit: maxItemsReached,
        }).then(res => res?.data),
      ]).then(res => {
        const [products] = res
        setDisplayResult({
          products,
        })
      })
    } else {
      setDisplayResult(null)
    }
  }, [debouncedSearchBarValue])

  useEffect(() => {
    if (inputRef) {
      inputRef.current?.focus()
    }
  }, [])

  const EmptyMessage = () => (
    <h3 className='EmptyMessage flex h-[48px] bg-white w-full items-center bg-grey-200 pl-[16px] text-base text-dark-400'>
      Đang tìm kiếm
    </h3>
  )

  return (
    <>
      <SearchIcon className='flex-[18px] max-w-[18px] cursor-pointer' />
      <input
        ref={inputRef}
        type='text'
        className={`w-full flex-1 h-full rounded-s-2 p-3 shadow-none placeholder:text-mainBody transition hover:cursor-pointer focus:bg-transparent focus:outline-none`}
        placeholder={'Bạn cần tìm gì hôm nay?'}
        value={searchBarValue}
        autoFocus={true}
        onChange={e => {
          setSearchBarValue(e.target.value)
          setIsComponentVisible(true)
        }}
        onClick={() => {
          if (router.pathname === '/search-result') {
            setSearchBarValue('')
            return router.push(`/search`)
          }
        }}
        onKeyUp={e => handleInputKeyUp(e)}
      />

      {searchBarValue ? (
        <>
          {' '}
          <span
            className='mr-[13px] cursor-pointer'
            onClick={() => {
              setSearchBarValue('')
              return router.push('/search')
            }}
          >
            <CloseSearchIcon className='h-[18px] flex-[18px] max-w-[18px]' />{' '}
          </span>
        </>
      ) : (
        <>
          <span
            onClick={() => {
              setIsComponentVisible(false)
              return router.push(`/search-result?query=${searchBarValue}`)
            }}
            className='ml-[10px] flex h-[30px] min-w-[56px] items-center justify-center bg-primary-base rounded-full cursor-pointer font-medium'
          >
            Tìm
          </span>
        </>
      )}

      {/* RESULTS */}
      {isComponentVisible && searchBarValue.length > 0 && (
        <div
          className='absolute left-0 h-[200%] top-[7px] z-[9999] mt-12 w-full rounded  shadow-gray-400 drop-shadow'
          ref={ref}
        >
          {displayResult ? (
            <>
              <div className='searchProductResult bg-white w-full'>
                <div className='searchProductResultContent grid'>
                  {displayResult.products?.map(el => (
                    <Link
                      key={el._id}
                      href={
                        '/' + getTranslate(el.slugs.value) + el.slugs.postfix
                      }
                      className='block'
                    >
                      <SearchProductItemCard
                        id={el._id}
                        finalPrice={el.finalPrice
                          .toLocaleString(selectedLang as string, {
                            style: 'currency',
                            currency: currency(selectedLang as string),
                          })
                          .replace(/\s₫/g, 'đ')}
                        salePrice={el.price
                          .toLocaleString(selectedLang as string, {
                            style: 'currency',
                            currency: currency(selectedLang as string),
                          })
                          .replace(/\s₫/g, 'đ')}
                        name={getTranslate(el.name)}
                        imgSrc={el.thumbnail}
                        imgAlt={getTranslate(el.name)}
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <EmptyMessage />
            </>
          )}
        </div>
      )}
    </>
  )
}
